import { useState, useEffect } from 'react';
import { loadGoogleAnalytics } from './services/gtagService';
import Navigation from './components/Common/Navigation/Navigation';
import ExchangePage from './pages/ExchangePage/ExchangePage';
import HistoryPage from './pages/HistoryPage/HistoryPage';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import AuthPage from './pages/AuthPage/AuthPage';
import { useTelegram } from './hooks/useTelegram';  
import { authAPI, alertAPI, ALERT_EVENTS } from './services/api';
import "./i18n";
import NewsPage from './pages/NewsPage/NewsPage';
import GlobalMessage from './components/Common/GlobalMessage/GlobalMessage';

const App = () => {
  const [globalMessage, setGlobalMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('exchange');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, showAlert, hapticFeedback, isInTelegram, startParam } = useTelegram();

  useEffect(() => {
    loadGoogleAnalytics();
    alertAPI.trigger(ALERT_EVENTS.BASE_LOAD_DONE);

    const handler = (event) => {
      setGlobalMessage({
        title: event.detail.title,
        content: event.detail.content,
      });
    };

    window.addEventListener('global-message', handler);

    return () => {
      window.removeEventListener('global-message', handler);
    };
  }, []);

  useEffect(() => {
    console.log('startParam:', startParam);
  }, [startParam]);

  const handleAuth = (authData) => {
    if (authData.success) {
      setIsAuthenticated(true);
      alertAPI.trigger(ALERT_EVENTS.CABINET_ENTER_INIT);
    }
  };

  const handleLogout = async () => {
    setLoading(true)
    try {
      const response = await authAPI.makeForbid();
    } catch (error) {
      console.error("logout error:", error);
      showAlert(error?.response?.data?.error);
      setIsAuthenticated(false);
      return false
    } finally {
      setLoading(false);
      setIsAuthenticated(false);
      setActiveTab('exchange');
      return true
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);

    const appContainer = document.querySelector('.app-container');
    const mainContent = document.querySelector('.main-content');

    const scrollContainer = appContainer || mainContent || window;

    if (scrollContainer && scrollContainer.scrollTo) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo(0, 0);
    }
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case 'exchange':
        return { title: 'T-Crypto', subtitle: 'Обмен криптовалют' };
      case 'history':
        return { title: 'T-Crypto', subtitle: 'История транзакций' };
      case 'profile':
        return { title: 'T-Crypto', subtitle: 'Профиль' };
      default:
        return { title: 'T-Crypto', subtitle: 'Обмен криптовалют' };
    }
  };

  const renderPage = () => {
    switch (activeTab) {
      case 'exchange':
        return <ExchangePage />;
      case 'history':
        return <HistoryPage />;
      case 'profile':
        return <ProfilePage onLogout={handleLogout} />;
      case 'news':
        return <NewsPage />;
      default:
        return <ExchangePage />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-500 to-blue-700 text-white p-5">
        <div className="text-3xl font-bold mb-8 tracking-tight text-white">
          T-Crypto
        </div>
        <div className="w-10 h-10 border-3 border-white/30 border-t-white rounded-full animate-spin mb-4"></div>
        <p className="text-base opacity-90 text-white">Загрузка...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="app">
        <AuthPage
          user={user}
          onAuth={handleAuth}
          hapticFeedback={hapticFeedback}
          showAlert={showAlert}
          isInTelegram={isInTelegram}
          startParam={startParam}
        />
        {globalMessage && (
          <GlobalMessage
            title={globalMessage.title}
            content={globalMessage.content}
            onClose={() => setGlobalMessage(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="app-container min-h-screen w-full bg-gray-50 flex flex-col max-w-full overflow-x-hidden
                    lg:max-w-md lg:mx-auto lg:border-x lg:border-gray-200"
      style={{ height: '100vh', height: 'calc(var(--vh, 1vh) * 100)' }}>

      <main className="main-content flex-1 px-4 pt-5 pb-20 overflow-y-auto bg-gray-50
                       sm:px-3 sm:pt-4 sm:pb-16
                       xs:px-2 xs:pt-3 xs:pb-14"
        style={{
          WebkitOverflowScrolling: 'touch',
          overflowY: 'auto',
          height: 'calc(100vh - 80px)',
          height: 'calc(calc(var(--vh, 1vh) * 100) - 80px)'
        }}>
        {renderPage()}
      </main>

      <Navigation activeTab={activeTab} setActiveTab={handleTabChange} />

      {globalMessage && (
        <GlobalMessage
          title={globalMessage.title}
          content={globalMessage.content}
          onClose={() => setGlobalMessage(null)}
        />
      )}
    </div>
  );
};

export default App;